import { useCallback, useEffect, useState } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useAccessToken } from '../auth/use-access-token.js';
import { apiFetch, ApiError, NotSignedIn } from './client.js';
import type { SignatureCodes } from './coach-signature.js';

/**
 * Everyone on the signed-in person's account.
 *
 * snake_case, because the API speaks the database's language (NM-03) and
 * translating at the boundary would mean two names for every field.
 */
export interface Person {
  client_id: string;
  first_name: string;
  last_name: string;
  /** YYYY-MM-DD. A birthday is a calendar date and never becomes an instant. */
  /** Null for an athlete a coach added until the parent gives it (stage C). */
  date_of_birth: string | null;
  is_account_holder: boolean;
  /** Only the account holder has one. */
  country: string | null;
  /** When the participation waiver was signed; null for the holder and children added before 13 October. */
  waiver_signed_at: string | null;
  /**
   * Signed on the participation waiver in force today (or there is none). False
   * is what makes the hour screen ask for it before the child goes in the cart.
   */
  waiver_current: boolean;
  /** The holder is an athlete: no children, or booked as one (W1). Always true for a child. */
  plays: boolean;
  /** The photo and video answer; null when never asked. */
  image_video_allowed: boolean | null;
  /** A child's own sign-in (migrations 016-017); null for the holder and a child never asked about. */
  sign_in: SignIn | null;
  /** A child's Performance Signature codes (stage 4b); null for the holder and until the coach enters one. */
  signature: SignatureCodes | null;
}

export interface SignIn {
  username: string | null;
  enabled: boolean;
  turned_on_at: string | null;
  turned_off_at: string | null;
  /** Set while a setup link this parent asked for is live. */
  link_until: string | null;
}

export type PeopleState =
  | { status: 'signed-out' }
  | { status: 'loading' }
  | { status: 'ready'; people: Person[] }
  | { status: 'failed'; code: string; message: string };

/**
 * Not a provider, unlike `me`.
 *
 * The account page and a person's page both read this, but they are different
 * routes and never render at once — so there is no second copy to disagree with
 * the first, and nothing to share. The moment something renders both together,
 * this becomes a provider the way MeProvider did.
 */
export function usePeople(): { state: PeopleState; reload: () => void } {
  const isAuthenticated = useIsAuthenticated();
  const getToken = useAccessToken();
  const [state, setState] = useState<PeopleState>({ status: 'signed-out' });
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!isAuthenticated) {
      setState({ status: 'signed-out' });
      return;
    }

    let cancelled = false;
    setState({ status: 'loading' });

    apiFetch<{ people: Person[] }>('/api/people', getToken)
      .then((body) => {
        if (!cancelled) setState({ status: 'ready', people: body.people });
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        if (cause instanceof NotSignedIn) {
          setState({ status: 'signed-out' });
        } else if (cause instanceof ApiError) {
          setState({ status: 'failed', code: cause.code, message: cause.message });
        } else {
          setState({
            status: 'failed',
            code: 'unreachable',
            message: 'The API did not answer. Is the Functions host running on port 7071?',
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, getToken, nonce]);

  return { state, reload };
}

/**
 * What the Add a child form holds, in the form's own names.
 *
 * camelCase in the request and snake_case in the response is not an oversight —
 * it is the same split `submitProfile` records: the body is the portal's shape,
 * the answer is the API's.
 */
export interface ChildDraft {
  firstName: string;
  lastName: string;
  /** YYYY-MM-DD, which is what an <input type="date"> yields. */
  dateOfBirth: string;
  /**
   * The boxes ticked, each with the words that were on the screen (ruled 13
   * October). Participation is required; media is null when left unticked.
   */
  consents: { participation: Consent | null; media: Consent | null };
}

export interface Consent {
  waiverId: string;
  text: string;
}

/**
 * The photo and video answer, changed on a child's page. A yes carries the
 * words shown, as when adding; a no is a withdrawal and carries nothing.
 */
export async function updateMediaConsent(
  getToken: () => Promise<string | null>,
  clientId: string,
  consent: Consent | null,
): Promise<{ image_video_allowed: boolean }> {
  return apiFetch(`/api/people/${encodeURIComponent(clientId)}/media-consent`, getToken, {
    method: 'PUT',
    body: JSON.stringify(consent ? { allowed: true, consent } : { allowed: false }),
  });
}

/**
 * Throws ApiError on refusal, carrying the code and — for a 422 — the field it
 * is about. The caller turns that into a sentence, because the sentence depends
 * on where it is shown.
 */
export async function addChild(
  getToken: () => Promise<string | null>,
  draft: ChildDraft,
): Promise<Person> {
  return apiFetch<Person>('/api/people', getToken, {
    method: 'POST',
    body: JSON.stringify(draft),
  });
}
